import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '@/store'
import { updatePreviewResponse } from '@/store/slices/preview'
import { cn } from '@/lib/utils'
import Tick from '@/assets/builder/icon/Tick'

interface Option {
    id: string
    text: string
}

interface Question {
    questionId: string
    config: {
        options?: Option[]
        allowOther?: boolean
        otherLabel?: string
        maxSelect?: number | null
    }
}

const MultiSelect: React.FC = () => {
    const dispatch = useDispatch()
    const { selectedQuestionId } = useSelector((state: any) => state.preview)
    // Get question data from Redux store
    const question = useSelector((state: RootState) =>
        state.preview.questions.find(
            (q: Question) => q.questionId === selectedQuestionId
        )
    )

    const savedResponse = useSelector(
        (state: RootState) => state.preview.previewResponses[selectedQuestionId]
    )

    // Extract options and other configuration from question
    const options = question?.config?.options || []
    const allowOther = question?.config?.allowOther || false
    const otherLabel = question?.config?.otherLabel || 'Other'
    const maxSelect = question?.config?.maxSelect || null

    const [selectedOptions, setSelectedOptions] = useState<string[]>(
        savedResponse?.optionIds || []
    )
    const [otherValue, setOtherValue] = useState<string>(
        savedResponse?.otherValue || ''
    )

    // Update local state when Redux store changes
    useEffect(() => {
        if (savedResponse) {
            setSelectedOptions(savedResponse.optionIds || [])
            setOtherValue(savedResponse.otherValue || '')
        } else {
            setSelectedOptions([])
            setOtherValue('')
        }
    }, [savedResponse])

    useEffect(() => {
        dispatch(
            updatePreviewResponse({
                questionId: selectedQuestionId,
                response: {
                    optionIds: selectedOptions,
                    otherValue: selectedOptions.includes('other')
                        ? otherValue
                        : '',
                },
            })
        )
    }, [selectedOptions, otherValue, dispatch, selectedQuestionId])

    const handleOptionToggle = (optionId: string) => {
        setSelectedOptions((prev) => {
            if (prev.includes(optionId)) {
                return prev.filter((id) => id !== optionId)
            } else {
                // Enforce max selection limit
                if (maxSelect && prev.length >= maxSelect) {
                    return prev
                }
                return [...prev, optionId]
            }
        })
    }

    const handleOtherChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setOtherValue(e.target.value)
        if (selectedOptions.includes('other')) {
            dispatch(
                updatePreviewResponse({
                    questionId: selectedQuestionId,
                    response: {
                        optionIds: selectedOptions,
                        otherValue: e.target.value,
                    },
                })
            )
        }
    }

    // If question not found, return null
    if (!question) return null

    const isMaxReached = maxSelect ? selectedOptions.length >= maxSelect : false

    return (
        <div className="relative checkbox-group mt-6 flex flex-col gap-[14px]">
            {maxSelect && (
                <p className={`text-[12px] font-[400] ${isMaxReached ? 'text-[#dc2626]' : 'text-[#6B7280]'}`}>
                    {isMaxReached
                        ? `Maximum of ${maxSelect} selected`
                        : `Select up to ${maxSelect}`}
                </p>
            )}
            {options.map((option: Option, index: number) => (
                <div
                    key={`preview-modal-option-${selectedQuestionId}-${option.id}-${index}`}
                    className="checkbox-option"
                >
                    <CheckboxButton
                        key={`preview-modal-checkbox-${selectedQuestionId}-${option.id}-${index}`}
                        id={`${selectedQuestionId}-${option.id}`}
                        checked={selectedOptions.includes(option.id)}
                        onChange={() => handleOptionToggle(option.id)}
                        label={
                            option?.text !== ''
                                ? option.text
                                : `Response ${index + 1}`
                        }
                        disabled={isMaxReached && !selectedOptions.includes(option.id)}
                    />
                </div>
            ))}

            {allowOther && (
                <div className="relative checkbox-option">
                    <CheckboxButton
                        key={`preview-modal-other-${selectedQuestionId}-checkbox`}
                        id={`${selectedQuestionId}-other`}
                        checked={selectedOptions.includes('other')}
                        onChange={() => handleOptionToggle('other')}
                        label={otherLabel}
                        disabled={isMaxReached && !selectedOptions.includes('other')}
                    />
                    {selectedOptions.includes('other') && (
                        <input
                            key={`preview-modal-other-input-${selectedQuestionId}`}
                            type="text"
                            value={otherValue}
                            onChange={handleOtherChange}
                            className={cn(
                                'mt-2 ml-7 w-[414px] outline-none rounded-md placeholder-[#616161] border border-[0.5px] border-[#8A8A8A] bg-background px-3 py-2 text-sm shadow-none',
                                otherValue !== '' ? 'font-[550]' : 'font-[400]'
                            )}
                            placeholder="Please specify"
                        />
                    )}
                </div>
            )}
        </div>
    )
}

interface CheckboxButtonProps {
    id: string
    checked: boolean
    onChange: () => void
    label: string
    disabled?: boolean
}

const CheckboxButton: React.FC<CheckboxButtonProps> = ({
    id,
    checked,
    onChange,
    label,
    disabled = false,
}) => {
    return (
        <label
            htmlFor={`${id}-preview`}
            key={`preview-modal-label-${id}-${checked}`}
            className={`flex items-center text-[14px] font-[400] select-none gap-[11px] ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
        >
            <div className="relative flex-shrink-0 w-[18px] h-[18px]">
                <input
                    type="checkbox"
                    id={`${id}-preview`}
                    key={`preview-modal-input-${id}-${checked}`}
                    checked={checked}
                    onChange={disabled ? undefined : onChange}
                    disabled={disabled}
                    className="sr-only"
                />
                <span
                    className={`inline-flex items-center justify-center w-[18px] h-[18px] rounded-[4px] transition-colors ${checked ? 'bg-[#015AD1]' : 'border border-[#DEDEDE]'
                        } group-hover:bg-gray-300`}
                >
                    {checked && <Tick />}
                </span>
            </div>
            <span>{label}</span>
        </label>
    )
}

export default MultiSelect
